import { CSSProperties, useCallback, useEffect, useRef, useState } from "react";
import { Conversion, LiveLogsResponseDto, MessageType, ResponseStatus, User } from "../utils/types";
import { useAbortController } from "../hooks/useAbortController";
import { LiveLogs } from "../services/LiveLogs";
import { Alert, Tab, Table, Tabs } from "react-bootstrap";
import dayjs from "../utils/dayjs";
import Loading from "../utils/Loding";
import { joinURLs } from "../utils/urlUtil";
import TimeAgo from "timeago-react";
import { PagesHelper } from "../common/PagesHelper";
import TagsFilterAutocomplete from "../components/autocompletes/tags/TagsFilterAutocomplete";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';

interface LiveLogsPageProps {
  currentUser: User
}

const REFRESH_INTERVAL_VISIBLE = 10000; // 10 seconds

const getPayout = (conversion:Conversion) => {
  return conversion.costPerInstall ? 1.0 * (conversion.costPerInstall) : 0.0;
}
const getPartnerPayout = (conversion:Conversion) => {
  return conversion.partnerPayoutForInstall ? 1.0 * (conversion.partnerPayoutForInstall) : 0.0;
}
const getMargin = (conversion:Conversion) => {
  const payout = getPayout(conversion);
  const partnerPayout = getPartnerPayout(conversion);
  return (payout - partnerPayout) / (payout) * 100;
}
const noWrapStyle: CSSProperties = {
  whiteSpace: "nowrap"
};
const noWrapCenterStyle: CSSProperties = {
  whiteSpace: "nowrap",
  textAlign: "center"
}
const tzTdStyle: CSSProperties = {
  whiteSpace: "nowrap",
  textAlign: "right",
  minWidth: "3rem"
}
const campaignIdTdStyle: CSSProperties = {
  whiteSpace: "nowrap",
  maxWidth: "10em",
  overflow: "hidden",
  textOverflow: "ellipsis"
}

function LiveLogsPage(props: LiveLogsPageProps) {
  const [liveLogsDto, setLiveLogsDto] = useState<LiveLogsResponseDto>(null);
  const [paused, setPaused] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  const refreshTimeoutRef = useRef<number | null>(null);
  const isPageVisible = useRef(!document.hidden);
  const filterTagsRef = useRef(null);
  const { createController: createDemandSourcesController } = useAbortController();
  const { createController: createLiveLogsController } = useAbortController();
  
  const handleTagsChange = (selected:any) => {
    const newTags = selected.map((item:any) =>
      item.customOption ? item.label : item
    );

    setTags(Array.from(new Set(newTags)));
  }
  const handleTagsKeyDown = (e:any) => {
    if (e.code === "Tab" || e.code === "Enter" || e.code === "Space" || e.code === "Comma" || e.code === "NumpadEnter") {
      e.preventDefault();

      const newTag = (e.target as HTMLInputElement).value.trim();
      if (!newTag) return;

      setTags(prev => prev.includes(newTag) ? prev : [...prev, newTag]);

      filterTagsRef.current.setState({
        ...filterTagsRef.current.state,
        text: ""
      });
    }
  }
  const filreBy = (option: any, props: any) => {
    if ((option as any).customOption) return true;
    return option.toLowerCase().includes(props.text.toLowerCase());
  }

  const scheduleNextRefresh = useCallback(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    if (document.hidden) return;

    const controller = createLiveLogsController();
    refreshTimeoutRef.current = window.setTimeout(() => {
      LiveLogs.getLiveLogs(controller, tags, setLiveLogsDto, scheduleNextRefresh);
    }, REFRESH_INTERVAL_VISIBLE);
  }, [tags, createLiveLogsController]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      isPageVisible.current = !document.hidden;
      setPaused(document.hidden);
      if (!document.hidden) {
        const controller = createLiveLogsController();
        LiveLogs.getLiveLogs(controller, tags, setLiveLogsDto, scheduleNextRefresh);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [scheduleNextRefresh, tags, createLiveLogsController]);

  useEffect(() => {
    if (props.currentUser) {
      const demandSourcesController = createDemandSourcesController();
      PagesHelper.loadDemandSourcesHelper(demandSourcesController);
      const liveLogsController = createLiveLogsController();
      LiveLogs.getLiveLogs(liveLogsController, tags, setLiveLogsDto, scheduleNextRefresh);
    }
    return () => {
      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
    };
  }, [props.currentUser, scheduleNextRefresh, tags, createDemandSourcesController, createLiveLogsController]);


  if (!liveLogsDto) return <Loading />;
  if (liveLogsDto && liveLogsDto.status === ResponseStatus.ERROR) {
    return (
      <Alert variant={MessageType.DANGER}>
        <span>{liveLogsDto && liveLogsDto.error ? liveLogsDto.error : "Error loading Live Logs"}</span>
      </Alert>
    );
  }

  return (
    <div style={{ height: "calc(100vh - 6.3rem)", overflow: "hidden" }}>
      {
        paused ? (
          <div>Auto-update is paused</div>
        ) : (
          <>
            <div style={{ position: "relative", marginTop: "1rem", marginBottom: "1rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <FontAwesomeIcon icon={faMagnifyingGlass} style={{ color: "#999" }} />
                <TagsFilterAutocomplete
                  onChange={handleTagsChange}
                  onKeyDown={handleTagsKeyDown}
                  placeholder="Filter by tags"
                  options={tags}
                  selected={tags}
                  filterBy={filreBy}
                  reactRef={filterTagsRef}
                  style={{ maxWidth: "525px" }}
                />
              </div>

              {liveLogsDto?.status === ResponseStatus.LOADING && (
                <div style={{ position: "absolute", right: "50%", top: "50%", transform: "translateY(-50%) scale(0.5)", pointerEvents: "none", zIndex: 10 }}>
                  <Loading />
                </div>
              )}
            </div>

            <Tabs defaultActiveKey="conversions" id="logs-tabs" className="mb-3">
              <Tab eventKey="conversions" title="Conversions">
                <div style={{ height: 'calc(100vh - 250px)', overflowY: 'auto' }}>
                  <Table className="live-logs-table" style={{ fontSize: 11 }} striped bordered hover>
                    <thead>
                      <tr>
                        <th style={noWrapStyle}>TS</th>
                        <th style={noWrapStyle}>ConvTS</th>
                        <th style={noWrapStyle}>ClickTS</th>
                        <th style={noWrapCenterStyle}>TZ</th>
                        <th style={noWrapStyle}>User ID</th>
                        <th style={noWrapStyle}>Click ID</th>
                        <th style={noWrapStyle}>Publisher ID</th>
                        <th style={noWrapStyle}>Site ID</th>
                        <th style={noWrapStyle}>Placement</th>
                        <th style={noWrapStyle}>User IP</th>
                        <th style={noWrapStyle}>App ID</th>
                        <th style={noWrapStyle}>Tracker</th>
                        <th style={noWrapStyle}>Campaign ID</th>
                        <th style={noWrapStyle}>Event</th>
                        <th style={noWrapStyle}>Country</th>
                        <th style={noWrapStyle}>Payout</th>
                        <th style={noWrapStyle}>P2P</th>
                        <th style={noWrapStyle}>Margin</th>
                        <th style={noWrapStyle}>cLink ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {
                        liveLogsDto.data.conversions.reverse().map((conversion, index:number) => {
                          return (
                            <tr key={index} className="{{conversion.clickId}}">
                              <td style={tzTdStyle}>
                                <TimeAgo title={dayjs.utc(conversion.timestampReceived).local().format("YYYY-MM-DD hh:mm:ss") + " (local time)"} datetime={dayjs.utc(conversion.timestampReceived).toDate()} locale='en_US'/>
                              </td>
                              <td style={tzTdStyle}>
                                <TimeAgo title={dayjs.utc(conversion.timestamp).local().format("YYYY-MM-DD hh:mm:ss") + " (local time)"} datetime={dayjs.utc(conversion.timestamp).toDate()} locale='en_US'/>
                              </td>
                              <td style={tzTdStyle}>
                                <TimeAgo title={dayjs.utc(conversion.clickTime).local().format("YYYY-MM-DD hh:mm:ss") + " (local time)"} datetime={dayjs.utc(conversion.clickTime).toDate()} locale='en_US'/>
                              </td>
                              <td>{conversion.clientTimezoneInfo?.utcOffsetMinutes}</td>
                              <td style={noWrapStyle}>
                                <a href={joinURLs('/admin/user?userID=' + conversion.globalUserId, process.env.REACT_APP_JUPITER_API_BASE_URL)} target="_blank" rel="noreferrer">
                                  {conversion.globalUserId.slice(0, 5)} ... {conversion.globalUserId.slice(-5)}
                                </a>
                              </td>
                              <td>
                                <a href={joinURLs('/admin/click?clickID=' + conversion.clickId, process.env.REACT_APP_JUPITER_API_BASE_URL)}>
                                  {conversion.clickId}
                                </a>
                              </td>
                              <td>{conversion.publisherId}</td>
                              <td>{conversion.siteId}</td>
                              <td style={noWrapStyle}>{conversion.reachExtensionInfo.effectivePartnerPlacement}</td>
                              <td>{conversion.httpHeaderInfo.userIP}</td>
                              <td style={noWrapStyle}>{conversion.appImpressionInfo.appID}</td>
                              <td style={noWrapStyle}>{PagesHelper.getDemandSourceLabel(conversion.trackingPartner)}</td>
                              <td style={campaignIdTdStyle}>{conversion.campaignId.replace(/[|].*/, "")}</td>
                              <td style={noWrapStyle}>{conversion.partnerEventID}</td>
                              <td>{conversion.httpHeaderInfo.geoIPCountry}</td>
                              <td>{getPayout(conversion).toFixed(2)}</td>
                              <td>{getPartnerPayout(conversion).toFixed(2)}</td>
                              <td>{getMargin(conversion).toFixed(0)}%</td>
                              <td style={noWrapStyle}>{conversion.campaignLinkID}</td>
                            </tr>
                          );
                        })
                      }
                    </tbody>
                  </Table>
                </div>
              </Tab>

              <Tab eventKey="clicks" title="Clicks">
                <div style={{ height: 'calc(100vh - 250px)', overflowY: 'auto' }}>
                  <Table className="live-logs-table" style={{ fontSize: 11 }} striped bordered hover>
                    <thead>
                      <tr>
                        <th style={noWrapStyle}>TS</th>
                        <th style={noWrapCenterStyle}>TZ</th>
                        <th style={noWrapStyle}>User ID</th>
                        <th style={noWrapStyle}>Click ID</th>
                        <th style={noWrapStyle}>Publisher ID</th>
                        <th style={noWrapStyle}>Site ID</th>
                        <th style={noWrapStyle}>Placement</th>
                        <th style={noWrapStyle}>User IP</th>
                        <th style={noWrapStyle}>App ID</th>
                        <th style={noWrapStyle}>Tracker</th>
                        <th style={noWrapStyle}>Campaign ID</th>
                        <th style={noWrapStyle}>Country</th>
                        <th style={noWrapStyle}>cLink ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {
                        liveLogsDto.data.clicks.reverse().map((click, index:number) => {
                          return (
                            <tr key={index} className={click.clickId}>
                              <td style={tzTdStyle}>
                                <TimeAgo title={dayjs.utc(click.timestamp).local().format("YYYY-MM-DD hh:mm:ss") + " (local time)"} datetime={dayjs.utc(click.timestamp).toDate()} locale='en_US'/>
                              </td>
                              <td>{click.clientTimezoneInfo?.utcOffsetMinutes}</td>
                              <td style={noWrapStyle}>
                                <a href={joinURLs('/admin/user?userID=' + click.globalUserId, process.env.REACT_APP_JUPITER_API_BASE_URL)} target="_blank" rel="noreferrer">
                                  {click.globalUserId.slice(0, 5)} ... {click.globalUserId.slice(-5)}
                                </a>
                              </td>
                              <td>
                                <a href={joinURLs('/admin/click?clickID=' + click.clickId, process.env.REACT_APP_JUPITER_API_BASE_URL)}>
                                  {click.clickId}
                                </a>
                              </td>
                              <td>{click.publisherId}</td>
                              <td>{click.siteId}</td>
                              <td style={noWrapStyle}>
                                {click.reachExtensionInfo.effectivePartnerPlacement}
                              </td>
                              <td>{click.httpHeaderInfo.userIP}</td>
                              <td>{click.appImpressionInfo.appID}</td>
                              <td style={noWrapStyle}>
                                {PagesHelper.getDemandSourceLabel(click.trackingPartner)}
                              </td>
                              <td style={campaignIdTdStyle}>{click.campaignId.replace(/[|].*/, "")}</td>
                              <td>{click.httpHeaderInfo.geoIPCountry}</td>
                              <td>{click.campaignLinkID}</td>
                            </tr>
                          );
                        })
                      }
                    </tbody>
                  </Table>
                </div>
              </Tab>
            </Tabs>
          </>
        )
      }
    </div>
  );
}

export default LiveLogsPage;